<template>
  <div class="course-detail">
    <!-- Hero Banner -->
    <div class="course-hero px-6 py-5 mb-5 rounded-lg">
      <div class="flex items-start justify-between">
        <div class="flex items-center gap-2 mb-3">
          <v-btn icon dark small @click="$emit('back')" class="mr-1">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <h1 class="text-2xl font-bold text-white mb-1">{{ course.title }}</h1>
            <div class="flex items-center gap-3 flex-wrap">
              <v-chip small dark color="rgba(255,255,255,0.2)" class="font-semibold">
                <v-icon left x-small>mdi-tag</v-icon>{{ course.category }}
              </v-chip>
              <v-chip small dark :color="difficultyColor" class="font-semibold">
                <v-icon left x-small>mdi-signal</v-icon>{{ course.difficulty }}
              </v-chip>
              <span class="text-white text-sm opacity-80">
                <v-icon x-small dark class="mr-1">mdi-account-tie</v-icon>{{ course.instructor }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex gap-2 align-center justify-end">
          <!-- Download Certificate Button -->
          <v-btn
            v-if="myProgress === 100 && isSelfEnrolled"
            small
            color="success"
            class="normal-case font-bold rounded-lg"
            @click="downloadCertificate"
          >
            <v-icon left small>mdi-file-certificate</v-icon>Download Certificate
          </v-btn>

          <!-- Publish Course Button -->
          <v-btn
            v-if="!isCoursePublished"
            small
            color="success"
            class="normal-case font-bold rounded-lg"
            :loading="publishLoading"
            v-iam-disabled="'learning.courses.update'"
            @click="publishCourse"
          >
            <v-icon left small>mdi-cloud-upload</v-icon>Publish Course
          </v-btn>
          <v-chip
            v-else
            small
            color="rgba(76, 175, 80, 0.2)"
            class="white--text font-bold border border-green-500 rounded-lg"
          >
            <v-icon left small color="#4caf50">mdi-check-decagram</v-icon>Published
          </v-chip>

          <v-btn small color="primary" dark class="normal-case rounded-lg" v-iam-disabled="'learning.courses.update'" @click="editCourse">
            <v-icon left small>mdi-pencil</v-icon>Edit
          </v-btn>
          <v-btn
            small
            dark
            class="normal-case rounded-lg"
            color="red lighten-2"
            v-iam-disabled="'learning.courses.delete'"
            @click="confirmDelete"
          >
            <v-icon left small>mdi-delete</v-icon>Delete
          </v-btn>
        </div>
      </div>

      <!-- Stats Row -->
      <div class="flex gap-5 flex-wrap">
        <div class="hero-stat">
          <v-icon small dark class="mr-1">mdi-book-multiple</v-icon>
          <span>{{ course.lessons.length }} Lessons</span>
        </div>
        <div class="hero-stat">
          <v-icon small dark class="mr-1">mdi-clock-outline</v-icon>
          <span>{{ totalDuration }}</span>
        </div>
        <div class="hero-stat">
          <v-icon small dark class="mr-1">mdi-account-group</v-icon>
          <span>{{ course.enrolledUsers.length }} Enrolled</span>
        </div>
        <div class="hero-stat">
          <v-icon small dark class="mr-1">mdi-check-circle</v-icon>
          <span>{{ myProgress }}% Complete</span>
        </div>
      </div>

      <!-- Progress Bar -->
      <v-progress-linear
        :value="myProgress"
        color="white"
        height="6"
        rounded
        class="mt-3"
        background-color="rgba(255,255,255,0.25)"
      />
    </div>
    <p class="text-sm opacity-90 my-4 max-w-3xl">{{ course.description }}</p>
    <v-divider></v-divider>
    <div class="flex gap-5 mt-4">
      <!-- Left: Lesson List -->
      <div class="flex-1">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-bold" style="color: #21427d">
            <v-icon class="mr-1" color="#21427d">mdi-format-list-bulleted</v-icon>
            Course Content
          </h2>
          <v-btn
            color="#21427d"
            small
            class="white--text normal-case rounded-lg"
            depressed
            v-iam-disabled="'learning.courses.create'"
            @click="showLessonForm = true"
          >
            <v-icon left small>mdi-plus</v-icon>Add Lesson
          </v-btn>
        </div>

        <!-- Accordion Lesson List -->
        <v-expansion-panels v-model="openPanels" multiple flat class="lesson-accordion">
          <v-expansion-panel
            v-for="(lesson, index) in sortedLessons"
            :key="lesson._id"
            class="lesson-panel mb-2 rounded-lg"
          >
            <v-expansion-panel-header class="lesson-panel-header py-2 px-4">
              <div class="flex items-center gap-3 w-full">
                <!-- Completion Checkbox -->
                <v-checkbox
                  :disabled="!isSelfEnrolled"
                  :input-value="isLessonDone(lesson._id)"
                  @change="toggleLessonCompletion(lesson._id, $event)"
                  hide-details
                  class="ma-0 pa-0 shrink-0"
                  color="#4caf50"
                  @click.stop
                />

                <!-- Lesson Number Badge -->
                <div
                  class="lesson-number flex-shrink-0 text-xs font-bold"
                  :style="{
                    width: '28px',
                    height: '28px',
                    minWidth: '28px',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isLessonDone(lesson._id) ? 'white' : '#21427d',
                    background: isLessonDone(lesson._id) ? '#4caf50' : '#e8eef8'
                  }"
                >
                  {{ index + 1 }}
                </div>

                <!-- Lesson Title -->
                <div class="flex-1 min-w-0">
                  <div
                    class="font-semibold text-sm"
                    :class="{ 'line-through text-gray-400': isLessonDone(lesson._id) }"
                  >
                    {{ lesson.title }}
                  </div>
                </div>

                <!-- Duration & Status actions (Right aligned) -->
                <div class="flex items-center justify-end gap-3 ml-auto flex-shrink-0">
                  <!-- Video status chip -->
                  <v-chip
                    v-if="lesson.hlsStatus"
                    small
                    :color="lesson.hlsStatus === 'READY' ? 'success lighten-4' : 'orange lighten-4'"
                    :text-color="
                      lesson.hlsStatus === 'READY' ? 'success darken-2' : 'orange darken-3'
                    "
                    class="font-medium"
                  >
                    <v-icon small left>{{
                      lesson.hlsStatus === 'READY' ? 'mdi-video-check' : 'mdi-clock-outline'
                    }}</v-icon>
                    {{ lesson.hlsStatus === 'READY' ? 'HLS Ready' : 'Processing' }}
                  </v-chip>

                  <!-- Duration chip -->
                  <v-chip small outlined color="#21427d" class="font-medium">
                    <v-icon small left>mdi-clock-outline</v-icon>{{ lesson.duration }}
                  </v-chip>

                  <!-- Play button -->
                  <v-btn icon color="#21427d" @click.stop="playLesson(lesson)">
                    <v-icon size="28">mdi-play-circle</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="px-4 pb-3">
              <div class="pl-14">
                <p class="text-sm text-gray-600 mb-3">{{ lesson.description }}</p>
                <div class="flex flex-wrap gap-2">
                  <v-btn
                    small
                    outlined
                    color="#21427d"
                    class="normal-case"
                    @click="playLesson(lesson)"
                  >
                    <v-icon left x-small>mdi-play</v-icon>Play Lesson
                  </v-btn>
                  <v-btn
                    small
                    outlined
                    color="orange darken-2"
                    class="normal-case"
                    v-iam-disabled="'learning.courses.update'"
                    @click="openVideoUpload(lesson)"
                  >
                    <v-icon left x-small>mdi-upload</v-icon>Upload Video
                  </v-btn>
                  <v-btn
                    small
                    outlined
                    color="red darken-2"
                    class="normal-case"
                    v-iam-disabled="'learning.courses.update'"
                    @click="openPdfUpload(lesson)"
                  >
                    <v-icon left x-small>mdi-file-pdf-box</v-icon>Upload PDF
                  </v-btn>
                  <v-btn small outlined class="normal-case" v-iam-disabled="'learning.courses.update'" @click="editLesson(lesson)">
                    <v-icon left x-small>mdi-pencil</v-icon>Edit
                  </v-btn>
                  <v-btn
                    small
                    outlined
                    color="red"
                    class="normal-case"
                    v-iam-disabled="'learning.courses.delete'"
                    @click="deleteLessonConfirm(lesson)"
                  >
                    <v-icon left x-small>mdi-delete</v-icon>Remove
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div v-if="course.lessons.length === 0" class="text-center py-10">
          <v-icon size="48" color="#bdbdbd" class="mb-3">mdi-book-open-variant</v-icon>
          <p class="text-gray-500">No lessons yet. Add your first lesson to get started!</p>
        </div>
      </div>

      <!-- Right Sidebar - Enrolled Users -->
      <div class="course-sidebar" style="width: 280px">
        <v-btn
          v-if="!isSelfEnrolled"
          color="#21427d"
          class="white--text normal-case rounded-lg w-full mb-4 py-5 font-bold"
          depressed
          :loading="enrollLoading"
          @click="enrollInCourse"
        >
          <v-icon left>mdi-account-plus</v-icon>Enroll in Course
        </v-btn>
        <v-btn
          v-else-if="myProgress === 100"
          color="orange darken-2"
          class="white--text normal-case rounded-lg w-full mb-4 py-5 font-bold"
          depressed
          :loading="enrollLoading"
          @click="retakeCourse"
        >
          <v-icon left>mdi-sync</v-icon>Retake Course
        </v-btn>
        <v-chip
          v-else
          color="success"
          outlined
          label
          class="w-full justify-center mb-4 py-4 font-bold rounded-lg"
        >
          <v-icon left small>mdi-check-decagram</v-icon>Enrolled
        </v-chip>

        <!-- <v-card class="rounded-lg" outlined>
          <v-card-title class="text-sm font-bold pb-2" style="color: #21427d">
            <v-icon small class="mr-2" color="#21427d">mdi-account-group</v-icon>
            Enrolled Users
          </v-card-title>
          <v-divider />
          <v-list dense class="pa-0">
            <v-list-item v-for="uid in course.enrolledUsers" :key="uid" class="py-1">
              <v-list-item-avatar size="30">
                <v-avatar size="30" color="#21427d">
                  <span class="white--text text-xs font-bold">{{ getUserInitials(uid) }}</span>
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-sm">{{ getUserName(uid) }}</v-list-item-title>
                <v-list-item-subtitle class="text-xs"
                  >{{ getUserProgress(uid) }}% complete</v-list-item-subtitle
                >
              </v-list-item-content>
              <v-list-item-action>
                <v-progress-circular
                  :value="getUserProgress(uid)"
                  :size="28"
                  :width="3"
                  :color="getProgressColor(getUserProgress(uid))"
                >
                  <span style="font-size: 8px; font-weight: 700">{{ getUserProgress(uid) }}</span>
                </v-progress-circular>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <div v-if="!course.enrolledUsers.length" class="pa-4 text-center">
            <p class="text-sm text-gray-400">No users enrolled yet</p>
          </div>
        </v-card> -->

        <!-- Course Info Card -->
        <v-card class="rounded-lg mt-4" outlined>
          <v-card-title class="text-sm font-bold pb-2" style="color: #21427d">
            <v-icon small class="mr-2" color="#21427d">mdi-information-outline</v-icon>
            Course Info
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-3">
            <div class="info-row mb-2">
              <span class="text-xs text-gray-500">Category</span>
              <v-chip x-small color="#e8eef8" text-color="#21427d">{{ course.category }}</v-chip>
            </div>
            <div class="info-row mb-2">
              <span class="text-xs text-gray-500">Difficulty</span>
              <v-chip x-small :color="difficultyColor" dark>{{ course.difficulty }}</v-chip>
            </div>
            <div class="info-row mb-2">
              <span class="text-xs text-gray-500">Instructor</span>
              <span class="text-xs font-semibold">{{ course.instructor }}</span>
            </div>
            <div class="info-row">
              <span class="text-xs text-gray-500">Created</span>
              <span class="text-xs font-semibold">{{ formatDate(course.createdAt) }}</span>
            </div>
          </v-card-text>
        </v-card>

        <!-- Assign Course Card -->
        <v-card class="rounded-lg mt-4" outlined>
          <v-card-title class="text-sm font-bold pb-2" style="color: #21427d">
            <v-icon small class="mr-2" color="#21427d">mdi-account-multiple-outline</v-icon>
            Assign Course
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-3">
            <v-autocomplete
              v-model="assignments.targetDepartments"
              :items="departments"
              item-text="departmentName"
              item-value="_id"
              label="Target Departments"
              multiple
              chips
              small-chips
              deletable-chips
              outlined
              dense
              hide-details
              class="mb-3"
            />

            <v-autocomplete
              v-model="assignments.targetTeams"
              :items="teams"
              item-text="teamName"
              item-value="_id"
              label="Target Teams"
              multiple
              chips
              small-chips
              deletable-chips
              outlined
              dense
              hide-details
              class="mb-3"
            />

            <v-autocomplete
              v-model="assignments.targetUsers"
              :items="users"
              item-text="name"
              item-value="_id"
              label="Target Users"
              multiple
              chips
              small-chips
              deletable-chips
              outlined
              dense
              hide-details
              class="mb-3"
            />

            <v-btn
              color="#21427d"
              class="white--text normal-case rounded-lg w-full font-bold"
              depressed
              small
              :loading="saveAssignmentsLoading"
              @click="saveAssignments"
            >
              Save Assignments
            </v-btn>
          </v-card-text>
        </v-card>
      </div>
    </div>

    <!-- Lesson Form Dialog -->
    <LessonFormDialog
      v-model="showLessonForm"
      :editData="editLessonData"
      :courseId="course.rawCourse ? course.rawCourse._id : course._id"
      :moduleId="
        course.rawCourse && course.rawCourse.modules && course.rawCourse.modules[0]
          ? course.rawCourse.modules[0]._id
          : null
      "
      :lessonId="editLessonData ? editLessonData._id : null"
      @save="handleLessonSave"
    />

    <!-- Standalone Video Upload for existing lessons -->
    <VideoUploadDialog
      v-if="showVideoUploadForLesson && videoUploadLesson"
      v-model="showVideoUploadForLesson"
      :courseId="course.rawCourse ? course.rawCourse._id : course._id"
      :moduleId="videoUploadLesson.moduleId"
      :lessonId="videoUploadLesson._id"
      @uploaded="onVideoUploaded"
    />

    <!-- Standalone PDF Upload for existing lessons -->
    <PdfUploadDialog
      v-if="showPdfUploadForLesson && pdfUploadLesson"
      v-model="showPdfUploadForLesson"
      :courseId="course.rawCourse ? course.rawCourse._id : course._id"
      :moduleId="pdfUploadLesson.moduleId"
      :lessonId="pdfUploadLesson._id"
      @uploaded="onPdfUploaded"
    />

    <!-- Course Edit Dialog -->
    <CourseFormDialog v-model="showCourseEditForm" :editData="course" @save="handleCourseEdit" />

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteConfirm" max-width="400">
      <v-card class="pa-6">
        <v-card-title class="px-0 pt-0 pb-4 border-b d-flex align-center">
          <v-icon left color="red" class="mr-2">mdi-alert-circle</v-icon>
          <span class="text-base font-bold text-gray-800">{{
            deleteType === 'course' ? 'Delete Course' : 'Delete Lesson'
          }}</span>
          <v-spacer />
          <v-btn icon @click="showDeleteConfirm = false">
            <v-icon color="grey darken-2">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="px-0 pt-4 pb-0 mt-2">
          Are you sure you want to delete <strong>{{ deleteTargetName }}</strong
          >? This action cannot be undone.
        </v-card-text>
        <v-card-actions class="px-0 pt-4 pb-0 d-flex justify-end">
          <v-spacer />
          <v-btn text class="normal-case rounded-lg" @click="showDeleteConfirm = false"
            >Cancel</v-btn
          >
          <v-btn
            color="red"
            dark
            class="normal-case rounded-lg px-5"
            depressed
            @click="executeDelete"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  state,
  isLessonCompleted,
  markLessonComplete,
  markLessonIncomplete,
  getCourseProgress,
  getUserById,
  updateCourse,
  deleteCourse,
  addLesson,
  updateLesson,
  deleteLesson,
  initLearningData,
  selfEnroll
} from './learningData'
import LessonFormDialog from './LessonFormDialog.vue'
import CourseFormDialog from './CourseFormDialog.vue'
import VideoUploadDialog from './VideoUploadDialog.vue'
import PdfUploadDialog from './PdfUploadDialog.vue'

export default {
  name: 'CourseDetail',
  components: { LessonFormDialog, CourseFormDialog, VideoUploadDialog, PdfUploadDialog },
  props: {
    course: { type: Object, required: true },
    currentUserId: { type: String, default: 'u1' }
  },
  data() {
    return {
      openPanels: [],
      showLessonForm: false,
      showCourseEditForm: false,
      editLessonData: null,
      showDeleteConfirm: false,
      deleteType: 'course',
      deleteTargetId: null,
      deleteTargetName: '',
      showVideoUploadForLesson: false,
      videoUploadLesson: null,
      showPdfUploadForLesson: false,
      pdfUploadLesson: null,
      enrollLoading: false,
      publishLoading: false,
      assignments: {
        targetDepartments: [],
        targetTeams: [],
        targetUsers: []
      },
      departments: [],
      teams: [],
      users: [],
      saveAssignmentsLoading: false
    }
  },
  watch: {
    course: {
      handler() {
        this.initAssignments()
      },
      deep: true,
      immediate: true
    }
  },
  async created() {
    await this.fetchMetadata()
  },
  computed: {
    isCoursePublished() {
      return this.course.rawCourse?.isPublished || false
    },
    isSelfEnrolled() {
      return this.course.enrolledUsers.includes(this.currentUserId)
    },
    sortedLessons() {
      return [...this.course.lessons].sort((a, b) => a.order - b.order)
    },
    totalDuration() {
      let totalMinutes = 0
      this.course.lessons.forEach((l) => {
        const parts = l.duration.split(':')
        totalMinutes += parseInt(parts[0] || 0)
        totalMinutes += parseInt(parts[1] || 0) / 60
      })
      const hrs = Math.floor(totalMinutes / 60)
      const mins = Math.round(totalMinutes % 60)
      return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`
    },
    myProgress() {
      void state.progress.length
      return getCourseProgress(this.course._id, this.currentUserId)
    },
    myEnrollment() {
      void state.enrollments.length
      return state.enrollments.find(
        (e) =>
          (e.enrolledUser?._id || e.enrolledUser)?.toString() === this.currentUserId.toString() &&
          (e.course?._id || e.course)?.toString() === this.course._id.toString()
      )
    },
    difficultyColor() {
      const map = { Beginner: '#4caf50', Intermediate: '#ff9800', Advanced: '#f44336' }
      return map[this.course.difficulty] || '#9e9e9e'
    }
  },
  methods: {
    initAssignments() {
      const mappedUsers = (this.course.targetUsers || [])
        .map((tu) => {
          return typeof tu.userId === 'object' ? tu.userId?._id : tu.userId
        })
        .filter(Boolean)

      const mappedDeps = (this.course.targetDepartments || [])
        .map((d) => {
          return typeof d === 'object' ? d._id : d
        })
        .filter(Boolean)

      const mappedTeams = (this.course.targetTeams || [])
        .map((t) => {
          return typeof t === 'object' ? t._id : t
        })
        .filter(Boolean)

      this.assignments = {
        targetDepartments: mappedDeps,
        targetTeams: mappedTeams,
        targetUsers: mappedUsers
      }
    },
    async fetchMetadata() {
      try {
        const [depRes, teamRes] = await Promise.all([
          this.$axios.get('/orgSetup/departments'),
          this.$axios.get('/teams')
        ])
        this.departments = depRes.data.departments || []
        this.teams = teamRes.data.teams || []
        this.users = state.users || []
      } catch (err) {
        console.error('Error fetching metadata:', err)
      }
    },
    async saveAssignments() {
      this.saveAssignmentsLoading = true
      try {
        const payload = {
          ...this.course,
          targetDepartments: this.assignments.targetDepartments,
          targetTeams: this.assignments.targetTeams,
          targetUsers: this.assignments.targetUsers.map((uid) => ({ userId: uid }))
        }
        await updateCourse(this.$axios, this.course._id, payload)
        await initLearningData(this.$axios)
        this.$toast.success('Course assignments saved successfully!')
      } catch (err) {
        console.error(err)
        this.$toast.error(err.response?.data?.message || err.message || 'Failed to save assignments')
      } finally {
        this.saveAssignmentsLoading = false
      }
    },
    isLessonDone(lessonId) {
      void state.progress.length
      return isLessonCompleted(this.course._id, lessonId, this.currentUserId)
    },
    async enrollInCourse() {
      this.enrollLoading = true
      try {
        await selfEnroll(this.$axios, this.course._id, this.currentUserId)
        await initLearningData(this.$axios)
        this.$toast.success('Successfully enrolled in course!')
      } catch (err) {
        console.error(err)
        this.$toast.error(err.message || 'Failed to enroll')
      } finally {
        this.enrollLoading = false
      }
    },
    async retakeCourse() {
      this.enrollLoading = true
      try {
        await selfEnroll(this.$axios, this.course._id, this.currentUserId, true)
        await initLearningData(this.$axios)
        this.$toast.success('Successfully registered for course retake!')
      } catch (err) {
        console.error(err)
        this.$toast.error(err.message || 'Failed to retake course')
      } finally {
        this.enrollLoading = false
      }
    },
    async publishCourse() {
      this.publishLoading = true
      try {
        await this.$axios.put(`/learning_center/courses/${this.course._id}/publish`)
        if (this.course.rawCourse) {
          this.course.rawCourse.isPublished = true
        }
        this.$toast.success('Course published successfully! Users can now enroll.')
      } catch (err) {
        console.error(err)
        this.$toast.error(err.response?.data?.message || err.message || 'Failed to publish course')
      } finally {
        this.publishLoading = false
      }
    },
    async toggleLessonCompletion(lessonId, checked) {
      if (checked) {
        await markLessonComplete(this.$axios, this.course._id, lessonId, this.currentUserId)
      } else {
        await markLessonIncomplete(this.$axios, this.course._id, lessonId, this.currentUserId)
      }
      await initLearningData(this.$axios)
    },
    async downloadCertificate() {
      if (!this.myEnrollment) return
      try {
        const response = await this.$axios.get(
          `/learning_center/enrollments/${this.myEnrollment._id}/certificate`,
          {
            responseType: 'blob'
          }
        )
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = `Certificate-${this.course.title.replace(/\s+/g, '_')}.pdf`
        link.click()
        this.$toast.success('Certificate downloaded successfully!')
      } catch (err) {
        console.error(err)
        this.$toast.error('Failed to download certificate')
      }
    },
    playLesson(lesson) {
      this.$emit('play-lesson', lesson)
    },
    editCourse() {
      this.showCourseEditForm = true
    },
    async handleCourseEdit(formData) {
      await updateCourse(this.$axios, this.course._id, formData)
      await initLearningData(this.$axios)
    },
    confirmDelete() {
      this.deleteType = 'course'
      this.deleteTargetName = this.course.title
      this.showDeleteConfirm = true
    },
    editLesson(lesson) {
      this.editLessonData = lesson
      this.showLessonForm = true
    },
    deleteLessonConfirm(lesson) {
      this.deleteType = 'lesson'
      this.deleteTargetId = lesson._id
      this.deleteTargetName = lesson.title
      this.showDeleteConfirm = true
    },
    async executeDelete() {
      if (this.deleteType === 'course') {
        await deleteCourse(this.$axios, this.course._id)
        await initLearningData(this.$axios)
        this.$emit('back')
      } else {
        await deleteLesson(this.$axios, this.course._id, this.deleteTargetId)
        await initLearningData(this.$axios)
      }
      this.showDeleteConfirm = false
    },
    async handleLessonSave(formData) {
      if (this.editLessonData) {
        await updateLesson(this.$axios, this.course._id, this.editLessonData._id, formData)
        this.editLessonData = null
      } else {
        await addLesson(this.$axios, this.course._id, formData)
      }
      await initLearningData(this.$axios)
    },
    openVideoUpload(lesson) {
      this.videoUploadLesson = lesson
      this.showVideoUploadForLesson = true
    },
    openPdfUpload(lesson) {
      this.pdfUploadLesson = lesson
      this.showPdfUploadForLesson = true
    },
    onVideoUploaded() {
      // Refresh course data so new video content is visible
      initLearningData(this.$axios)
    },
    onPdfUploaded() {
      // Refresh course data so new PDF content is visible
      initLearningData(this.$axios)
    },
    getUserName(uid) {
      const user = getUserById(uid)
      return user ? user.name : uid
    },
    getUserInitials(uid) {
      const user = getUserById(uid)
      if (!user) return '?'
      return user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase()
    },
    getUserProgress(uid) {
      void state.progress.length
      return getCourseProgress(this.course._id, uid)
    },
    getProgressColor(progress) {
      if (progress >= 100) return '#4caf50'
      if (progress >= 60) return '#2196f3'
      if (progress >= 30) return '#ff9800'
      return '#f44336'
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.course-hero {
  background: linear-gradient(135deg, #21427d 0%, #3a6bc5 60%, #5e8fd6 100%);
  position: relative;
  overflow: hidden;
}
.course-hero::before {
  content: '';
  position: absolute;
  top: -60px;
  right: -60px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
}
.course-hero::after {
  content: '';
  position: absolute;
  bottom: -40px;
  left: 30%;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
}
.hero-stat {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.8rem;
  font-weight: 600;
}

.lesson-accordion .lesson-panel {
  border: 1px solid #e8eef8;
  transition: all 0.2s ease;
}
.lesson-accordion .lesson-panel:hover {
  border-color: #21427d;
  box-shadow: 0 2px 8px rgba(33, 66, 125, 0.08);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.course-sidebar {
  flex-shrink: 0;
}

@media (max-width: 960px) {
  .course-sidebar {
    width: 100% !important;
  }
}
</style>
